import React, { useState, useEffect, useRef } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { usePostHog } from "posthog-js/react";
import { useSourceContext } from "../../contexts/SourcesContext";
import { sourcesPanelStyles } from "../PanelStyles";

import SourcesHeader from "./SourcesHeader";
import SourceTypeToggle from "./SourceTypeToggle";
import SourceCard from "./SourceCard";
import {
  Source,
  SourceType,
  ScientificSource,
  MateriomFormulationSource,
  SourceTypeValues,
} from "../../types/frontend";

/**
 * Props for the SourcesPanel component.
 */
interface SourcesPanelProps {
  isPanelExpanded: boolean; // Indicates if the panel is expanded
  setIsPanelExpanded: React.Dispatch<React.SetStateAction<boolean>>; // Function to toggle panel expansion
  hideToggle?: boolean; // Optionally hide the source type toggle
  emptyStateMessage?: string; // Custom message when no sources are available
  enableAttachSources?: boolean; // Optionally enable attaching sources
}

/**
 * SourcesPanel Component
 *
 * Renders a panel for managing and displaying sources with options to toggle source types
 * and attach/detach sources.
 *
 * @param {SourcesPanelProps} props - Component props
 * @returns {JSX.Element} Rendered SourcesPanel component
 */
const SourcesPanel: React.FC<SourcesPanelProps> = ({
  isPanelExpanded,
  setIsPanelExpanded,
  hideToggle = false,
  enableAttachSources = true,
  emptyStateMessage = "Select your source preference and start a chat.",
}) => {
  // Access source context values
  const {
    sources,
    invertedSources,
    sourceType,
    setSourceType,
    attachedSources,
    setAttachedSources,
  } = useSourceContext();
  const [isLoading] = useState(false); // State to manage loading status
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null); // State to track expanded source card

  // PostHog hook to track source panel citations
  const posthog = usePostHog();
  // Ref to track which sources have already been logged
  const loggedSourceIdsRef = useRef<Set<string>>(new Set());

  /**
   * Track when sources are displayed in the panel and log citations to PostHog.
   * Only logs each source once to avoid duplicate events.
   */
  useEffect(() => {
    if (sources.length > 0) {
      sources.forEach((source) => {
        // Only log sources that haven't been logged yet
        if (!loggedSourceIdsRef.current.has(source.id)) {
          if (source.type === SourceTypeValues.ScientificSource) {
            posthog.capture("sourcePanelScientificSourceServed", {
              citation_url: (source as ScientificSource).url,
              citation_title: (source as ScientificSource).title,
            });
          } else if (source.type === SourceTypeValues.MateriomFormulation) {
            posthog.capture("sourcePanelMateriomFormulationSourceServed", {
              citation_url: (source as MateriomFormulationSource).url,
              citation_title: (source as MateriomFormulationSource).name,
            });
          }
          loggedSourceIdsRef.current.add(source.id);
        }
      });
    }
  }, [sources, posthog]);

  /**
   * Handle the change event for the source type toggle.
   * Updates the source type in the context.
   *
   * @param {React.MouseEvent<HTMLElement>} event - The mouse event
   * @param {SourceType | null} newValue - The new source type value
   */
  const handleToggleChange = (
    event: React.MouseEvent<HTMLElement>,
    newValue: SourceType | null
  ) => {
    if (newValue !== null) {
      setSourceType(newValue);
    }
  };

  /**
   * Handle the click event for a source card.
   * Toggles the expansion state of the source card.
   *
   * @param {any} source - The source object
   */
  const handleSourceClick = (source: any) => {
    if (expandedCardId === source.id) {
      setExpandedCardId(null);
      setIsPanelExpanded(false);
    } else {
      setExpandedCardId(source.id);
      setIsPanelExpanded(true);
    }
  };

  /**
   * Handle the click event for the checkbox in a source card.
   * Toggles the attachment state of the source.
   *
   * @param {Source} source - The source object
   * @param {React.MouseEvent} e - The mouse event
   */
  const onCheckboxClick = (source: Source, e: React.MouseEvent) => {
    e.stopPropagation();
    // If the source is already attached, remove it
    if (attachedSources.includes(source)) {
      handleRemoveSource(source);
    } else {
      setAttachedSources((sources) => [...sources, source]);
    }
  };

  /**
   * Remove a source from the attached sources list.
   *
   * @param {Source} source - The source object to remove
   */
  const handleRemoveSource = (source: Source) => {
    setAttachedSources((sources) => sources.filter((s) => s.id !== source.id));
  };

  /**
   * Handle the unexpand action for the panel.
   * Resets the expanded card state and collapses the panel.
   */
  const handleUnexpand = () => {
    setExpandedCardId(null);
    setIsPanelExpanded(false);
  };

  /**
   * Handle the click event for the "View source" link in a source card.
   * Opens the source URL in a new tab and stops event propagation.
   *
   * @param {React.MouseEvent} e - The mouse event
   * @param {Source} source - The source object
   */
  const handleLinkClick = (e: React.MouseEvent, source: Source) => {
    e.stopPropagation();
    window.open(
      (source as ScientificSource | MateriomFormulationSource).url,
      "_blank",
      "noopener,noreferrer"
    );

    // Log the link click in PostHog
    if (source.type === SourceTypeValues.ScientificSource) {
      posthog.capture("sourcePanelScientificSourceLinkClicked", {
        citation_url: (source as ScientificSource).url,
        citation_title: (source as ScientificSource).title,
      });
    } else if (source.type === SourceTypeValues.MateriomFormulation) {
      posthog.capture("sourcePanelMateriomFormulationSourceLinkClicked", {
        citation_url: (source as MateriomFormulationSource).url,
        citation_title: (source as MateriomFormulationSource).name,
      });
    }
  };

  return (
    <Box sx={sourcesPanelStyles.container}>
      <SourcesHeader
        isPanelExpanded={isPanelExpanded}
        handleUnexpand={handleUnexpand}
      />
      {!hideToggle && (
        <Box sx={{ pl: 2.5, pr: 2.5, pb: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{ color: "text.secondary", mb: 0.5 }}
          >
            I am interested in searching:
          </Typography>
          <SourceTypeToggle
            sourceType={sourceType}
            onChange={handleToggleChange}
          />
        </Box>
      )}
      <Box sx={sourcesPanelStyles.body}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : invertedSources.length === 0 ? (
          <Typography sx={sourcesPanelStyles.noSourcesText}>
            {emptyStateMessage}
          </Typography>
        ) : (
          invertedSources.map((source, index) => (
            <SourceCard
              key={source.id || `${source.id}-${index}`}
              source={source}
              isAttached={attachedSources.includes(source)}
              isAttachable={enableAttachSources}
              onClick={() => handleSourceClick(source)}
              onCheckboxClick={(e) => onCheckboxClick(source, e)}
              onLinkClick={handleLinkClick}
              expandedCardId={expandedCardId}
              isPanelExpanded={isPanelExpanded}
              index={index}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

export default SourcesPanel;
